import React, { useEffect, useMemo, useRef } from 'react';
import { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { REDUCTION_COMMENTS_SECTION_CONFIG } from './sections.config';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import { useMagicReport } from '../context/MagicReportContext';
import { ReductionCommentsTable, ReductionRow, roundRecommendedPercent } from './ReductionCommentsTable';
import { FieldType } from '../magic-report.type';
import { formatCurrency } from '../../../../common/utils/common.util';
import { calculateTotalCompletedStored } from '../../../project/project.util';
import { drawScopedKey } from './draw-scoped-sections.util';

interface ReductionCommentsSectionProps {
  magicReportId: string;
  /** The draw this copy of the section belongs to. */
  drawId: string;
}

const ROWS_FIELD = 'ReductionCommentsRows';
const ZERO_WORK_COMMENTS_FIELD = 'ZeroWorkComments';
const PARTIAL_REDUCTION_COMMENTS_FIELD = 'PartialReductionComments';

const generateZeroWorkComments = (rows: ReductionRow[]): string => {
  if (!rows.length) return '';

  return rows
    .map((row, index) => {
      const lineNumber = index + 1;
      const completedThisPeriod = formatCurrency(row.requestedAmount.toString(), true);
      const percentComplete = Math.round(row.percentRequested);
      const description = row.description || '[Description]';

      return `${lineNumber}- The Contractor is requesting ${completedThisPeriod} and a total of ${percentComplete}% of the ${description} line item, whereas we observed no work at the site. Our recommendation is to release nothing for this line item this period.`;
    })
    .join('\n\n');
};

const generatePartialReductionComments = (rows: ReductionRow[]): string => {
  if (!rows.length) return '';

  return rows
    .map((row, index) => {
      const lineNumber = index + 1;
      const completedThisPeriod = formatCurrency(row.requestedAmount.toString(), true);
      const percentComplete = Math.round(row.percentRequested);
      const inspectorComplete = roundRecommendedPercent(row.percentRecommended, row.percentRequested);
      const description = row.description || '[Description]';
      const fundingAmount = formatCurrency(row.fundingAmount.toString(), true);
      const reducedBy = formatCurrency(row.reducedBy.toString(), true);

      return `${lineNumber}- The Contractor is requesting ${completedThisPeriod} and a total of ${percentComplete}% of the ${description} line item, whereas we observed this work at ${inspectorComplete}% complete. Our recommendation is to release ${fundingAmount} for this line item, ${reducedBy} less than the application amount before retainage.`;
    })
    .join('\n\n');
};

const ReductionCommentsSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
  /** When set, rows are derived from this draw's line items instead of the primary draw's. */
  drawId?: string;
}> = ({ data, handleFieldChange, getField, onBook, drawId }) => {
  const parseRow = (value?: string): ReductionRow | null => {
    if (!value) return null;
    try {
      return JSON.parse(value) as ReductionRow;
    } catch {
      return null;
    }
  };

  const ensureRowDefaults = (row: Partial<ReductionRow>): ReductionRow => {
    const requestedAmount = row.requestedAmount ?? 0;
    const fundingAmount = row.fundingAmount ?? 0;
    const revisedBudget = row.revisedBudget ?? 0;
    const previousApplication = row.previousApplication ?? 0;

    const totalRequestedToDate = previousApplication + requestedAmount;
    const totalRecommendedToDate = previousApplication + fundingAmount;

    const percentRequested = row.percentRequested ?? (revisedBudget !== 0 ? (totalRequestedToDate / revisedBudget) * 100 : 0);
    const percentRecommended = row.percentRecommended ?? (revisedBudget !== 0 ? (totalRecommendedToDate / revisedBudget) * 100 : 0);

    return {
      lineItemId: row.lineItemId || '',
      description: row.description || '',
      requestedAmount,
      percentRequested,
      percentRecommended,
      fundingAmount,
      reducedBy: row.reducedBy ?? requestedAmount - fundingAmount,
      revisedBudget,
      previousApplication,
      conditionType: row.conditionType || 'partial',
    };
  };

  const { reportState, registerSectionSync, unregisterSectionSync } = useMagicReport();
  const commentsField = getCommentsField(data, {
    isCommentField: (name, type) => type === FieldType.TextArray && name === 'Comments',
  });

  // Scope to this draw's line items when the section is draw-scoped; otherwise fall back to the
  // primary-draw mirror so non-multi-draw callers behave as before.
  const lineItems = useMemo(() => {
    if (!drawId) return reportState.lineItems;
    return reportState.draws?.find(draw => draw.drawId === drawId)?.lineItems ?? reportState.lineItems;
  }, [drawId, reportState.draws, reportState.lineItems]);
  const rowsField = getField(ROWS_FIELD);
  const hasInitializedCommentsRef = useRef(false);
  const currentRows: ReductionRow[] = rowsField?.entries?.map(e => parseRow(e.value)).filter((r): r is ReductionRow => r !== null) || [];

  useEffect(() => {
    // Ensure comments fields exist with default empty values so they render properly
    const zeroWorkField = getField(ZERO_WORK_COMMENTS_FIELD);
    if (!zeroWorkField) {
      handleFieldChange(ZERO_WORK_COMMENTS_FIELD, { value: '' });
    }
    const partialField = getField(PARTIAL_REDUCTION_COMMENTS_FIELD);
    if (!partialField) {
      handleFieldChange(PARTIAL_REDUCTION_COMMENTS_FIELD, { value: '' });
    }
  }, [getField, handleFieldChange]);

  // Register sync to refresh rows/comments when "Run this Month" is clicked
  useEffect(() => {
    const syncFn = async () => {
      const items = (lineItems || []).filter(li => !li.isDeleted);
      if (!items.length) return;

      const rows: ReductionRow[] = [];

      items.forEach(li => {
        const previousApplication = li.previousApplication || 0;
        const requestedIncrement = Math.round(((li.completeThisPeriod || 0) + (li.storedThisPeriod || 0)) * 100) / 100;
        const cdccApproved = Math.round((li.cdccApproved || 0) * 100) / 100;
        const completedStoredToDate = Math.round(calculateTotalCompletedStored(li) * 100) / 100;
        const revisedBudget = li.revisedBudget || li.originalBudget || 0;

        if (requestedIncrement <= 0) return;

        const isReduction = cdccApproved < requestedIncrement;
        const isNoWorkCondition = isReduction && completedStoredToDate === 0;
        const isPartialCondition = isReduction && completedStoredToDate !== 0;
        if (!isNoWorkCondition && !isPartialCondition) {
          return;
        }

        const conditionType: ReductionRow['conditionType'] = isNoWorkCondition ? 'no-work' : 'partial';

        // Always rebuild from the current line item data rather than merging the previously
        // saved row — otherwise fundingAmount/percentRequested/percentRecommended/reducedBy stay
        // frozen at their old values even after completeThisPeriod/storedThisPeriod change.
        rows.push(
          ensureRowDefaults({
            lineItemId: li.id,
            description: li.lineItem?.description || '',
            requestedAmount: requestedIncrement,
            fundingAmount: cdccApproved,
            revisedBudget,
            previousApplication,
            conditionType,
          }),
        );
      });

      const entries =
        rows.length === 0
          ? []
          : rows.map((row, index) => ({
              order: index,
              value: JSON.stringify(row),
            }));

      handleFieldChange(ROWS_FIELD, { entries });

      const zeroWorkRows = rows.filter(r => r.conditionType === 'no-work');
      const partialRows = rows.filter(r => r.conditionType === 'partial');
      handleFieldChange(ZERO_WORK_COMMENTS_FIELD, { value: generateZeroWorkComments(zeroWorkRows) });
      handleFieldChange(PARTIAL_REDUCTION_COMMENTS_FIELD, { value: generatePartialReductionComments(partialRows) });
    };

    registerSectionSync('reduction-comments', syncFn);
    return () => unregisterSectionSync('reduction-comments');
  }, [currentRows, lineItems, handleFieldChange, registerSectionSync, unregisterSectionSync, ensureRowDefaults]);

  const layout = useMemo(
    () => ({
      singleColumn: [
        {
          fieldName: ROWS_FIELD,
          type: 'custom-component' as const,
          label: '',
          renderComponent: () => (
            <ReductionCommentsTable
              rowsField={rowsField}
              handleFieldChange={handleFieldChange}
              lineItems={lineItems}
              onRowsSynced={updatedRows => {
                const zeroWorkRows = updatedRows.filter(r => r.conditionType === 'no-work');
                const partialRows = updatedRows.filter(r => r.conditionType === 'partial');

                const zeroWorkText = generateZeroWorkComments(zeroWorkRows);
                const partialText = generatePartialReductionComments(partialRows);

                handleFieldChange(ZERO_WORK_COMMENTS_FIELD, { value: zeroWorkText });
                handleFieldChange(PARTIAL_REDUCTION_COMMENTS_FIELD, { value: partialText });

                hasInitializedCommentsRef.current = true;
              }}
            />
          ),
        },
        {
          fieldName: ZERO_WORK_COMMENTS_FIELD,
          type: 'text' as const,
          label: 'No Work Comments',
        },
        {
          fieldName: PARTIAL_REDUCTION_COMMENTS_FIELD,
          type: 'text' as const,
          label: 'Reduction Comments',
        },
      ],
    }),
    [rowsField, lineItems, handleFieldChange],
  );

  return (
    <>
      <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />

      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange('Comments', updated)}
          title='Comments'
          onBook={onBook}
        />
      )}
    </>
  );
};

export const ReductionCommentsSection: React.FC<ReductionCommentsSectionProps> = ({ magicReportId, drawId }) => {
  // Each draw gets its own section id, so MagicReportSection stores, anchors and completes this
  // draw's comments independently — no changes needed inside the shared section component.
  const sectionConfig = useMemo(
    () => ({ ...REDUCTION_COMMENTS_SECTION_CONFIG, id: drawScopedKey(REDUCTION_COMMENTS_SECTION_CONFIG.id, drawId) }),
    [drawId],
  );

  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={sectionConfig}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <ReductionCommentsSectionContent
            data={data}
            handleFieldChange={handleFieldChange}
            getField={getField}
            onBook={onBook}
            drawId={drawId}
          />
        ),
      }}
    />
  );
};
